import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Table, Form, Button } from 'react-bootstrap';
import {
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getGroupedRowModel,
    getFacetedRowModel,
    useReactTable,
    getFilteredRowModel,
    getFacetedUniqueValues,
} from '@tanstack/react-table';
import { FaSortUp, FaSortDown, FaFilter } from "react-icons/fa";
import styles from './DataGrid.module.scss';
import { rankItem } from '@tanstack/match-sorter-utils';
import Icon from "./ui/Icon";
import {
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
    MdKeyboardDoubleArrowLeft,
    MdKeyboardDoubleArrowRight
} from "react-icons/md";
import { isDateObject, toDateTime } from "../utils/utils";

const FilterItem = ({ filter, columnFilter, onSelect, formatLabel }) => {
    const [active, setActive] = useState(false);

    const checkIsActive = () => {
        if (isDateObject(columnFilter)) {
            let filterDate = new Date(filter)
            return filterDate.getTime() === columnFilter.getTime()
        }
        return filter === columnFilter
    }

    useEffect(() => {
        let filterActive = checkIsActive();
        setActive(filterActive);
    }, [columnFilter, filter]);

    const filterLabel = (() => {
        if (typeof formatLabel === 'function') {
            return formatLabel(filter);
        }
        if (filter === null || filter === undefined) {
            return '—';
        }
        if (isDateObject(filter)) {
            return toDateTime(filter);
        }
        return filter;
    })();

    return (
        <li
            onClick={() => onSelect(filter)}
            className={active ? 'active' : ''}
        >
            {filterLabel}
        </li>
    )
}

const RowHeader = ({ table, header, gridRef }) => {
    const [width, setWidth] = useState(0);
    const thRef = useRef(null);
    const divRef = useRef(null);

    const [isFilterSelected, setFilterSelected] = useState(false);
    const [isDivVisible, setDivVisibility] = useState(false);

    const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(header.column.id);
    const columnMeta = header.column.columnDef.meta;
    const panelDistanceMaxFilter = columnMeta?.panelDistanceMaxFilter === true;

    const handleItemClick = (item) => {
        if (item === 'None') {
            header.column.setFilterValue('');
            setFilterSelected(false);
        }
        else {
            setWidth(thRef?.current.clientWidth + 1);
            if (isDateObject(firstValue)) {
                let dateObject = new Date(item);
                header.column.setFilterValue(dateObject);
            } else {
                header.column.setFilterValue(item);
            }
            setFilterSelected(true);
        }

        setDivVisibility(false);
    };

    const handleIconClick = (event) => {
        event.stopPropagation();
        setWidth(thRef?.current.clientWidth + 1);
        setDivVisibility(!isDivVisible);
    };

    const sortedUniqueValues = useMemo(
        () => {
            if (panelDistanceMaxFilter) {
                const maxMiles = Number(columnMeta.panelDistanceFilterMaxMiles) || 75;
                const step = Number(columnMeta.panelDistanceFilterStepMiles) || 5;
                const out = [];
                for (let n = step; n <= maxMiles; n += step) {
                    out.push(n);
                }
                return out;
            }
            let keysArr = header.column.getFacetedUniqueValues().keys();
            if (isDateObject(firstValue)) {
                keysArr = [...keysArr].map(key => {
                    return toDateTime(key)
                })
            }
            return Array.from(keysArr);
        },
        [
            panelDistanceMaxFilter,
            columnMeta?.panelDistanceFilterMaxMiles,
            columnMeta?.panelDistanceFilterStepMiles,
            header.column.getFacetedUniqueValues(),
            firstValue,
        ],
    )
    const sortedUniqueValuesWithNull = ['None', ...sortedUniqueValues];

    useEffect(() => {
        const handleClickOutside = (event) => {
            const icon = document.querySelector(`[data-filter='${header.id}']`);
            if (divRef.current && !divRef.current.contains(event.target) && !icon.contains(event.target)) {
                setDivVisibility(false);
            }
        };
        document.querySelectorAll('table').forEach(table => table.addEventListener('click', handleClickOutside));
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const scrollContainer = gridRef?.current?.parentNode;
        const syncWidth = () => {
            if (thRef?.current) {
                setWidth(thRef.current.clientWidth + 1);
            }
        };
        syncWidth();

        function handleResize() {
            syncWidth();
        }

        const handleScroll = () => {
            syncWidth();
        }

        window.addEventListener('resize', handleResize);
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        }
    }, [gridRef]);

    return (
        <th
            key={header.id}
            ref={thRef}
            className={header.isPlaceholder ? 'isPlaceholder' : ''}
            colSpan={header.colSpan}
            style={{
                width: header.column.columnDef.size ?? undefined,
                maxWidth: header.column.columnDef.maxSize ?? undefined,
                minWidth: header.column.columnDef.minSize ?? undefined,
            }}>
            {header.isPlaceholder ? null : (
                <div className={styles.thFilterAnchor}>
                    <div
                        className="d-flex flex-nowrap"
                        data-id={header.id}>
                        <div
                            {...{
                                className: header.column.getCanSort()
                                    ? 'cursor-pointer select-none flex-grow-1'
                                    : (header.subHeaders?.length > 0 ? 'flex-grow-1 text-center' : 'flex-grow-1'),
                                onClick:
                                    header.column.getToggleSortingHandler(),
                            }}>
                            {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                            )}
                            {header.column.getCanSort() ?
                                ({
                                    asc: (<FaSortUp className={`position-relative ${styles.sortUp}`} />),
                                    desc: (<FaSortDown className={`position-relative ${styles.sortDown}`} />),
                                }[header.column.getIsSorted().toString()] ?? (<span className={`d-inline-block ${styles.noSort}`}></span>))
                                : <></> // Remove empty span from columns that don't have sorting
                            }
                        </div>
                        {header.column.getCanFilter() ? (
                            <div
                                className={`${styles.filterParent} flex-shrink-0 cursor-pointer px-2 me-n2`}
                                onClick={handleIconClick}>
                                {!isFilterSelected ? (
                                    <Icon
                                        data-filter={header.id}
                                        icon="filter"
                                        size={16}
                                    />
                                ) : (
                                    <FaFilter
                                        data-filter={header.id}
                                        className={styles.filterSelected}
                                    />
                                )}
                            </div>
                        ) : null}
                    </div>
                    {isDivVisible && (
                        <div
                            ref={divRef}
                            className={`${styles.filterItemsParent} flex-shrink-0`}
                            style={{ width: Math.max(width, 140) }}>
                            <ul className={styles.dropdownContent}>
                                {sortedUniqueValuesWithNull?.map(
                                    (filter) => (
                                        <FilterItem
                                            key={`${header.id}-${filter === null || filter === undefined ? 'null' : String(filter)}`}
                                            columnFilter={header.column.getFilterValue()}
                                            filter={filter}
                                            formatLabel={columnMeta?.filterItemFormat}
                                            onSelect={() =>
                                                handleItemClick(filter)
                                            }
                                        />
                                    )
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </th>
    );
};

const RowCell = ({ cell, row, rowClick }) => {
    return (
        <td
            key={cell.id}
            onClick={() => {
                if (rowClick) {
                    rowClick(row.original);
                }
            }}
            style={{
                maxWidth: cell.column.columnDef.maxSize,
            }}
            data-id={cell.id}>
            {
                cell.getIsAggregated() ? (
                // If the cell is aggregated, use the Aggregated renderer for cell
                flexRender(
                    cell.column.columnDef.aggregatedCell,
                    cell.getContext(),
                )
            ) : cell.getIsPlaceholder() ? null : (
                // For cells with repeated values, render null
                // Otherwise, just render the regular cell
                flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext(),
                )
                )}
        </td>
    );
}

const NoData = ({ table, noRecordMessage }) => {
    return (
        <tr>
            <td
                className="text-center text-secondary py-2"
                colSpan={
                    table.getHeaderGroups()[
                    table.getHeaderGroups().length - 1
                        ].headers.length
                }>
                {noRecordMessage}
            </td>
        </tr>
    );
};

const Pagination = ({ table, pageSizes, resetTable, setResetTable }) => {
    return (
        <div
            className={`d-flex flex-wrap justify-content-between mt-3 ${styles.pagination}`}>
            <div className="d-flex align-items-center pt-1">
                <Form.Select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                    }}
                    size="sm"
                    className={`w-auto ${styles.paginationSelect}`}>
                    <>
                        {pageSizes.map((pageSize) => (
                            <option
                                key={pageSize}
                                value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </>
                </Form.Select>
                <span className="text-nowrap ms-2">per page</span>
            </div>
            {table.getPageCount() > 1 && (
                <div className="d-flex align-items-center pt-1">
                    {table.getPageCount() > 2 && (
                        <Button
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                            variant="link"
                            size="sm"
                            className="d-flex align-items-center ps-1">
                            <MdKeyboardDoubleArrowLeft />
                            <span className={`${styles.pageNavLabel} ms-1`}>
                                First
                            </span>
                        </Button>
                    )}
                    <Button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        variant="link"
                        size="sm"
                        className="d-flex align-items-center ps-1">
                        <MdKeyboardArrowLeft />
                        <span className={`${styles.pageNavLabel} ms-1`}>
                            Previous
                        </span>
                    </Button>
                    <span className="d-flex align-items-center mx-2">
                        <span>Page</span>
                        <Form.Control
                            type="number"
                            value={
                                resetTable === true ? '' : table.getState().pagination.pageIndex + 1
                            }
                            onChange={(e) => {
                                const page = e.target.value;
                                if (page === '' || page > table.getPageCount()) {
                                    setResetTable(true);
                                } else {
                                    setResetTable(false);
                                    table.setPageIndex(Number(page) - 1);
                                }
                            }}
                            size="sm"
                            className={`mx-2 ${styles.paginationInput}`}
                        />
                        <span className="text-nowrap">
                            of {table.getPageCount()}
                        </span>
                    </span>
                    <Button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        variant="link"
                        size="sm"
                        className="d-flex align-items-center pe-1">
                        <span className={`${styles.pageNavLabel} me-1`}>
                            Next
                        </span>
                        <MdKeyboardArrowRight />
                    </Button>
                    {table.getPageCount() > 2 && (
                        <Button
                            onClick={() =>
                                table.setPageIndex(table.getPageCount() - 1)
                            }
                            disabled={!table.getCanNextPage()}
                            variant="link"
                            size="sm"
                            className="d-flex align-items-center pe-1">
                            <span className={`${styles.pageNavLabel} me-1`}>
                                Last
                            </span>
                            <MdKeyboardDoubleArrowRight />
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}

const DataGrid = (
    {
        data,
        columns,
        enableFilters,
        rowClick,
        sort,
        filter,
        groupBy,
        columnDisplay,
        rowSubHeaderComponent,
        rowSubComponent,
        onExportDataChange,
        gridContainerClass = '',
        stickyHeader = false,
        pagination = true,
        disablePagination = false,
        enableNesting = false,
        isSubGrid = false,
        noRecordMessage = 'No data',
        getRowClassName,
    }
) => {

    const [grouping, setGrouping] = useState(groupBy || []);
    const [sorting, setSorting] = useState(sort || []);
    const [columnVisibility, setColumnVisibility] = useState(columnDisplay || {});
    const pageSizes = [10, 20, 30, 40, 50];

    const [columnFilters, setColumnFilters] = useState(filter || []);
    const [globalFilter, setGlobalFilter] = useState('');
    const [resetTable, setResetTable] = useState(false);
    const [expanded, setExpanded] = useState(!enableNesting ? true : {});
    const gridRef = useRef(null);
    let rowStyles = {
        hasSubRows: enableNesting ? 'hasExpandRows' : 'hasSubRows',
        isFlatRow: 'isFlatRow',
        isNestedFlatRow: enableNesting ? 'isExpandFlatRow' : 'isFlatRow'
    }

    const fuzzyFilter = (row, columnId, value, addMeta) => {
        const itemRank = rankItem(row.getValue(columnId), value);
        addMeta({
            itemRank,
        });
        return itemRank.passed;
    };

    /** Inclusive max: row value ≤ filterValue (numeric). Empty filter passes. Null row value does not pass. */
    const lteNumber = (row, columnId, filterValue) => {
        if (filterValue === '' || filterValue === null || filterValue === undefined) {
            return true;
        }
        const cap = Number(filterValue);
        if (!Number.isFinite(cap)) {
            return true;
        }
        const raw = row.getValue(columnId);
        if (raw === null || raw === undefined) {
            return false;
        }
        const v = Number(raw);
        if (!Number.isFinite(v)) {
            return false;
        }
        return v <= cap;
    };
    lteNumber.autoRemove = (val) => val === '' || val == null;

    const table = useReactTable({
        data,
        columns,
        autoResetExpanded: false,
        defaultColumn: {
            size: undefined,
            minSize: 60,
            maxSize: 300,
            sortDescFirst: false,
        },
        filterFns: {
            fuzzy: fuzzyFilter,
            lteNumber,
        },
        state: {
            expanded,
            grouping,
            sorting,
            columnVisibility,
            columnFilters,
            globalFilter,
        },
        onColumnFiltersChange: setColumnFilters,
        onGroupingChange: setGrouping,
        getSubRows: row => row.subRows,
        onSortingChange: setSorting,
        enableSortingRemoval: false,
        onColumnVisibilityChange: setColumnVisibility,
        onExpandedChange: setExpanded,
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        globalFilterFn: fuzzyFilter,
        onGlobalFilterChange: setGlobalFilter,
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        enableColumnFilters: enableFilters,
        manualPagination: !pagination,
    });

    // Get the row count
    const rowCount = table.getCoreRowModel().rows.length;

    useEffect(() => {
        if (onExportDataChange) {
            const sortedRows = table.getSortedRowModel().rows?.map((row) => row.original);
            onExportDataChange(sortedRows);
        }
    }, [sorting]);

    useEffect(() => {
        if (onExportDataChange) {
            const filteredRows = table.getFilteredRowModel().rows?.map((row) => row.original);
            onExportDataChange(filteredRows);
        }
    }, [columnFilters]);

    useEffect(() => {
        setColumnFilters(filter);
    }, [filter]);

    return (
        <>
            <div
                className={`${
                    styles.dataGridContainer
                } ${gridContainerClass} rounded bg-white position-relative ${
                    enableFilters !== false ? styles.hasFilters : ''
                } ${stickyHeader ? `${styles.dataGridStickyHead}${isSubGrid ? ` ${styles.dataGridStickyHeadSub}` : ''}` : ''}`}>
                <Table
                    bordered
                    striped
                    hover
                    responsive={!stickyHeader}
                    className={`${styles.dataGrid} ${isSubGrid && styles.subGrid} ${
                        stickyHeader ? styles.dataGridStickyTable : ''
                    }`}
                    ref={gridRef}>
                    <thead>
                        {table.getHeaderGroups()?.map((headerGroup) => (
                            <tr
                                className={`${styles.tableHeader} ${
                                    table.getHeaderGroups().length - 1 ===
                                    headerGroup.depth
                                        ? 'flatHeader'
                                        : 'hasSubHeaders'
                                }`}
                                key={headerGroup.id}>
                                {headerGroup.headers?.map((header) => (
                                    <RowHeader
                                        key={header.id}
                                        table={table}
                                        header={header}
                                        gridRef={gridRef}
                                    />
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.length === 0 && (
                            <NoData table={table} noRecordMessage={noRecordMessage} />
                        )}
                        {table.getRowModel().rows?.map((row) => (
                            <Fragment key={row.id}>
                                {enableNesting && row.depth !== 0 && !!rowSubComponent ? (
                                    // When nesting is enabled and custom row component is passed, it will render custom row component
                                    // When nesting is enabled and custom row component is not passed, it will display integrated sub-rows with 'isExpandFlatRow' style
                                    <></>
                                ) : (
                                    <tr
                                        className={[
                                            row.getCanExpand()
                                                ? rowStyles.hasSubRows
                                                : row.depth === 0
                                                  ? rowStyles.isFlatRow
                                                  : rowStyles.isNestedFlatRow,
                                            typeof getRowClassName === 'function'
                                                ? getRowClassName(row)
                                                : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        style={{ cursor: rowClick ? 'pointer' : 'auto', }}>
                                        {row.getVisibleCells()?.map((cell) => (
                                            <RowCell
                                                key={cell.id}
                                                cell={cell}
                                                row={row}
                                                rowClick={rowClick}
                                            />
                                        ))}
                                    </tr>
                                )}

                                {/*
                                    Use either header or row sub component, not both together.
                                    Use header sub component when only header needs to be inserted between aggregate row and flat row
                                    Use row sub component when custom component needs to be rendered for sub rows
                                */}
                                {row.getIsExpanded() && !!rowSubHeaderComponent &&
                                    rowSubHeaderComponent(row)
                                }

                                {row.getIsExpanded() && !!rowSubComponent &&
                                    rowSubComponent(row)
                                }
                            </Fragment>
                        ))}
                    </tbody>
                </Table>
            </div>
            {pagination && rowCount > pageSizes[0] && !disablePagination ? (
                <Pagination
                    table={table}
                    pageSizes={pageSizes}
                    resetTable={resetTable}
                    setResetTable={setResetTable}
                />
            ) : null}
        </>
    );
}

export default DataGrid;
